import { useForm, SubmitHandler, NestedValue, FormProvider } from 'react-hook-form';
import { Button, Grid } from '@mui/material/';
import { EmployeeDto, RoleDto } from '../../../api';
import useFormStyles from '../../../styles/useFormStyles';
import ComboBox, { ComboBoxOption } from '../../../components/form/controls/ComboBox';
import { useState } from 'react';
import { EmailField, SelectionBox, TextField } from '../../../components/form/react-hook-form-components/';
import { getComboBoxOptions, getComboBoxOption, roleComboBoxOptions } from '../../../utils/Utils';
import CreateRolePopup from '../../../components/form/popups/CreateRolePopup';
import React from 'react';

interface IEmployeeFormInputs {
  firstName: string | null;
  surname: string | null;
  email: string;
  manager: NestedValue<ComboBoxOption> | null;
  teamName: string | null;
  profileLink: string | null;
}

interface EmployeeFormProps {
  employees: EmployeeDto[];
  onSubmitHandler: (input: EmployeeDto) => void;
  onCancelHandler: () => void;
  employee?: EmployeeDto;
  roles: RoleDto[];
}

function EmployeeForm(props: EmployeeFormProps) {
  const { employees, onCancelHandler, employee, onSubmitHandler, roles } = props;
  const [email, setEmail] = useState<string>(employee?.email ?? '');
  const [roleOptions] = useState(roleComboBoxOptions(roles));
  const [managerOptions] = useState(getManagerOptions);
  const [role, setRole] = useState<ComboBoxOption | undefined>(getInitialRoleComboBoxOption);
  const [IsCreateFormOpen, SetIsCreateFormOpen] = useState(false);
  const [roleError, setRoleError] = useState('');
  const [roleNameSuggestion, setRoleNameSuggestion] = useState('');
  const methods = useForm<IEmployeeFormInputs>({
    defaultValues: {
      firstName: employee?.firstName,
      surname: employee?.lastName,
      manager: getManagerComboBoxOption(employee?.managerId!),
      teamName: employee?.teamName,
      profileLink: employee?.profileLink,
    },
  });
  const classes = useFormStyles();
  const { getValues, handleSubmit, setValue } = methods;

  function getManagerOptions() {
    const options = employees.reduce((filtered: ComboBoxOption[], e) => {
      if (e.id !== employee?.id) {
        filtered.push({
          label: `${e.firstName} ${e.lastName}`,
          value: e.id,
        } as ComboBoxOption);
      }
      return filtered;
    }, []);

    return options;
  }

  function getInitialRoleComboBoxOption() {
    if (!employee || !employee.roleId) return;

    let roleName, option;
    for (let i = 0; i < roles.length; i++) {
      if (roles[i].roleId === employee.roleId) {
        roleName = roles[i].name;
        option = {
          label: roleName,
          value: employee.roleId,
        } as ComboBoxOption;
      }
    }
    return option;
  }

  /**
   * Handles the role ComboBox selection changes.
   * @param option ComboBoxOption of the selected role. Else, null if an option is not selected.
   */
  function onRoleChange(option: ComboBoxOption | null) {
    setRole(option ?? undefined);

    if (option === undefined) {
      setRoleError('Option is undefined');
    }
  }

  /**
   * Handles the change taken place in the first name input field. When the first name changes, this should automatically
   * modify the username and email as these fields depend on the first name.
   */
  const handleFirstNameChange = (event: { target: { value: string } }) => {
    const name = event.target.value;
    const username = `${name}.${getValues('surname')}`;
    setValue('firstName', name, { shouldValidate: true });
    updateFormFields(username);
  };

  /**
   * Handles the change taken place in the surname input field. When the surname changes, this should automatically
   * modify the username and email fields as these fields depend on the surname.
   * @param event the change event
   */
  const handleSurnameChange = (event: { target: { value: string } }) => {
    const surname = event.target.value;
    const usernameSuggestion = `${getValues('firstName')}.${surname}`;
    setValue('surname', surname, { shouldValidate: true });
    updateFormFields(usernameSuggestion);
  };

  /**
   * Handles the change taken place in the email input field.
   * @param event the change event
   */
  const handleEmailChange = (event: { target: { value: string } }) => {
    const email = event.target.value;
    setEmail(email);
    setValue('email', email);
  };

  /**
   * This method should be called within the handleFirstNameChange and handleSurnameChange
   * methods. This method updates the username and email state and will trigger a re-render of the updated components.
   * @param usernameSuggestion a username suggestion
   */
  const updateFormFields = (usernameSuggestion: string) => {
    let email = '';

    if (usernameSuggestion === '.') {
      usernameSuggestion = '';
    } else {
      email = `${usernameSuggestion}@datacom.co.nz`;
    }

    setEmail(email);
    setValue('email', email, { shouldValidate: true });
  };

  /**
   * Generates a combobox option for a manager given an employee Id
   * @param managerId the manager's employee ID
   */
  function getManagerComboBoxOption(managerId: number) {
    const manager = employees.find(e => e.id === managerId);
    if (manager === undefined) return;

    return getComboBoxOption(manager);
  }

  const onSubmit: SubmitHandler<IEmployeeFormInputs> = data => {
    const values = {
      id: employee?.id,
      firstName: data.firstName,
      lastName: data.surname,
      email: data.email,
      teamName: data.teamName,
      managerId: data.manager?.value,
      roleId: role?.value,
      profileLink: data.profileLink,
    } as EmployeeDto;
    onSubmitHandler(values);
  };

  return (
    <div>
      <FormProvider {...methods}>
        <form id="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={1} width={'45rem'}>
            <Grid item container xs={6} direction="column" spacing={1.5}>
              <Grid item>
                <TextField name="firstName" label="First Name" onChange={handleFirstNameChange} required autoFocus />
              </Grid>

              <Grid item>
                <TextField name="surname" label="Surname" onChange={handleSurnameChange} required />
              </Grid>

              <Grid item>
                <EmailField name="email" label="Email" value={email} onChange={handleEmailChange} required />
              </Grid>

              <Grid item>
                <TextField name="profileLink" label="Profile" />
              </Grid>
            </Grid>

            <Grid item container xs={6} direction="column" spacing={1.5}>
              <Grid item>
                <TextField name="teamName" label="Team Name" />
              </Grid>

              <Grid item>
                <ComboBox
                  name="role"
                  label="Role"
                  value={role}
                  error={roleError}
                  onChange={onRoleChange}
                  options={roleOptions}
                  creatable
                  onCreatableClick={nameSuggestion => {
                    setRoleNameSuggestion(nameSuggestion);
                    SetIsCreateFormOpen(true);
                  }}
                />
              </Grid>

              <Grid item>
                <SelectionBox name="manager" label="Manager" options={managerOptions} />
              </Grid>
            </Grid>

            <Grid item container direction="row" justifyContent={'end'} spacing={1}>
              <Grid item>
                <Button variant="contained" size="large" className={classes.submitButton} type="submit">
                  {employee?.id === undefined ? 'Create' : 'Update'}
                </Button>
              </Grid>

              <Grid item>
                <Button variant="outlined" size="large" color="neutral" onClick={onCancelHandler}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
        <CreateRolePopup
          isOpen={IsCreateFormOpen}
          setIsOpen={SetIsCreateFormOpen}
          onSuccess={(role: RoleDto) => {
            const option = getComboBoxOption(role);
            setRole(option);
          }}
          roleNameSuggestion={roleNameSuggestion}
        />
      </FormProvider>
    </div>
  );
}
export default EmployeeForm;
